import React, { useState, useEffect } from 'react';
import { InputConfig, SummarizerConfig } from '../../../../types/summarizer';
import URLInput from './URLInput';
import FileInput from './FileInput';
import TextInput from './TextInput';
import { Link, FileUp, Type } from 'lucide-react';
import { UIConfig } from '../../../../types';

export * from './URLInput';
export * from './FileInput';
export * from './TextInput';

interface InputSectionProps {
  config: InputConfig;
  className?: string;
  primaryColor?: string;
  ui?: UIConfig;
  fullConfig?: SummarizerConfig; // Add full config to access outputs
}

const InputSection: React.FC<InputSectionProps> = ({
  config,
  className = '',
  primaryColor = '#3b82f6',
  fullConfig,
}) => {
  // Determine available input types
  const hasFileInput = config.file?.enabled;
  const hasUrlInput = config.url?.enabled;
  const hasTextInput = config.text?.enabled;

  // Check if summary is enabled - if not, hide summary options
  const isSummaryEnabled = fullConfig?.outputs?.summary !== false;
  const shouldShowSummaryOptions = config.showSummaryOptions !== false && isSummaryEnabled;
  const shouldShowSummaryType = config.showSummaryType !== false && isSummaryEnabled;
  const shouldShowTone = config.showTone !== false && isSummaryEnabled;

  // Calculate which tabs to show with better icons
  const availableTabs = [];
  if (hasUrlInput)
    availableTabs.push({
      id: 'url',
      label: 'Enter URL',
      icon: Link,
      description: 'Paste a link to summarize',
    });
  if (hasFileInput)
    availableTabs.push({
      id: 'file',
      label: 'Upload File',
      icon: FileUp,
      description: 'Upload a document or media file',
    });
  if (hasTextInput)
    availableTabs.push({
      id: 'text',
      label: 'Enter Text',
      icon: Type,
      description: 'Type or paste text content',
    });

  const showTabs = availableTabs.length > 1;

  // Default to first available tab
  const defaultTab = availableTabs[0]?.id || 'url';
  const [activeTab, setActiveTab] = useState<'file' | 'url' | 'text'>(defaultTab as any);

  // Auto-detect clipboard content on mount
  useEffect(() => {
    const detectClipboard = async () => {
      try {
        if (!navigator.clipboard || !showTabs) return;
        const text = await navigator.clipboard.readText();
        if (text) {
          // Check if it's a URL
          if (hasUrlInput && (text.startsWith('http://') || text.startsWith('https://'))) {
            setActiveTab('url');
          }
          // Check if it's substantial text
          else if (hasTextInput && text.length > 100) {
            setActiveTab('text');
          }
        }
      } catch (err) {
        // Clipboard access denied or not available
      }
    };
    detectClipboard();
  }, [hasUrlInput, hasTextInput, showTabs]);

  return (
    <div className={`input-section ${className}`}>
      {/* Enhanced Tab Navigation */}
      {showTabs && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded-lg">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 min-w-[120px] !border-none px-4 py-3 rounded-md font-medium text-sm transition-all duration-200 !outline-none !bg-background ${
                    isActive
                      ? '!bg-white hover:!bg-white shadow-sm transform scale-[1.02] '
                      : 'hover:!bg-gray-50 !bg-transparent'
                  }`}
                  style={{
                    color: isActive ? primaryColor : '#6b7280',
                    border: isActive ? `1px solid ${primaryColor}` : '1px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </div>
                  {isActive && (
                    <div className="hidden sm:inline-block text-xs mt-1 opacity-75">
                      {tab.description}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Render the appropriate input component */}
      {activeTab === 'url' && hasUrlInput && config.url && (
        <URLInput
          config={config.url}
          disabled={false}
          primaryColor={primaryColor}
          className="mb-4"
          submitButtonText={config.submitButtonText}
          showSummaryOptions={shouldShowSummaryOptions}
          showSummaryType={shouldShowSummaryType}
          showTone={shouldShowTone}
        />
      )}

      {activeTab === 'file' && hasFileInput && config.file && (
        <FileInput
          config={config.file}
          disabled={false}
          primaryColor={primaryColor}
          className="mb-4"
          submitButtonText={config.submitButtonText}
          showSummaryOptions={shouldShowSummaryOptions}
          showSummaryType={shouldShowSummaryType}
          showTone={shouldShowTone}
        />
      )}

      {activeTab === 'text' && hasTextInput && config.text && (
        <TextInput
          config={config.text}
          disabled={false}
          primaryColor={primaryColor}
          className="mb-4"
          submitButtonText={config.submitButtonText}
          showSummaryOptions={shouldShowSummaryOptions}
          showSummaryType={shouldShowSummaryType}
          showTone={shouldShowTone}
        />
      )}
    </div>
  );
};

export default InputSection;
